import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, UserPlus } from 'lucide-react';
import { Button } from '@ntconsult/aluma-react';
import { useCreateShare } from '../../hooks/useCreateShare';
import { useUpdateShare } from '../../hooks/useUpdateShare';
import { useRevokeShare } from '../../hooks/useRevokeShare';
import type {
  Share,
  ShareResourceType,
  SharePermission,
} from '../../types';
import { AddPersonForm } from './AddPersonForm';
import { PersonRow } from './PersonRow';

interface PeopleSectionProps {
  resourceType: ShareResourceType;
  resourceId: string;
  userShares: Share[];
  groupShares: Share[];
  canManage: boolean;
}

export function PeopleSection({
  resourceType,
  resourceId,
  userShares,
  groupShares,
  canManage,
}: PeopleSectionProps) {
  const { t } = useTranslation('shares');
  const [showAdd, setShowAdd] = useState(false);

  const { mutate: createShare, isPending: isCreating } = useCreateShare();
  const { mutate: updateShare } = useUpdateShare();
  const { mutate: revokeShare } = useRevokeShare();

  const existingUserIds = useMemo(
    () => userShares.map((s) => s.userId).filter((v): v is string => !!v),
    [userShares],
  );

  const handleGrant = (userId: string, permission: SharePermission) => {
    createShare(
      {
        kind: 'user',
        userId,
        resourceType,
        resourceId,
        permission,
      },
      { onSuccess: () => setShowAdd(false) },
    );
  };

  const isEmpty = userShares.length === 0 && groupShares.length === 0;

  return (
    <section>
      <header className="flex items-center justify-between mb-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Users className="w-4 h-4" aria-hidden="true" />
          {t('people.sectionTitle')}
        </h3>
        {canManage && !showAdd && (
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<UserPlus className="w-4 h-4" aria-hidden="true" />}
            onClick={() => setShowAdd(true)}
          >
            {t('people.add')}
          </Button>
        )}
      </header>

      {canManage && showAdd && (
        <AddPersonForm
          excludeUserIds={existingUserIds}
          pending={isCreating}
          onCancel={() => setShowAdd(false)}
          onGrant={handleGrant}
        />
      )}

      {isEmpty ? (
        <p className="text-sm text-foreground-muted py-3">
          {t('people.empty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {userShares.map((s) => (
            <PersonRow
              key={s.id}
              share={s}
              canManage={canManage}
              onChangeLevel={(level) =>
                updateShare({ id: s.id, data: { permission: level } })
              }
              onRevoke={() => revokeShare(s.id)}
            />
          ))}
          {groupShares.map((s) => (
            <PersonRow
              key={s.id}
              share={s}
              canManage={canManage}
              onChangeLevel={(level) =>
                updateShare({ id: s.id, data: { permission: level } })
              }
              onRevoke={() => revokeShare(s.id)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
